import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

/**
 * Detects if a URL is a YouTube or Vimeo link and returns embed info.
 * Returns { type: 'youtube'|'vimeo'|'direct'|null, embedUrl: string|null }
 */
export const detectVideoUrl = (url) => {
  if (!url) return { type: null, embedUrl: null };

  // YouTube patterns
  const ytMatch =
    url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/) ||
    url?.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
  if (ytMatch) {
    return {
      type: 'youtube',
      embedUrl: `https://www.youtube.com/embed/${ytMatch?.[1]}?autoplay=1&rel=0`
    };
  }

  // Vimeo patterns
  const vimeoMatch = url?.match(/(?:vimeo\.com\/)(\d+)/);
  if (vimeoMatch) {
    return {
      type: 'vimeo',
      embedUrl: `https://player.vimeo.com/video/${vimeoMatch?.[1]}?autoplay=1`
    };
  }

  // Direct video file (mp4, webm, etc.)
  if (/\.(mp4|webm|ogg|mov)(\?|$)/i?.test(url)) {
    return { type: 'direct', embedUrl: url };
  }

  return { type: null, embedUrl: null };
};

const VideoPlayerModal = ({ isOpen, onClose, webinar }) => {
  const [iframeError, setIframeError] = useState(false);

  if (!isOpen || !webinar) return null;

  const recordingUrl = webinar?.recording_url || webinar?.meeting_url;
  const { type, embedUrl } = detectVideoUrl(recordingUrl);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-4xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Play" size={16} color="var(--color-primary)" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground truncate">
                {webinar?.title}
              </h2>
              {webinar?.expert?.name && (
                <p className="text-xs text-muted-foreground truncate">
                  {webinar?.expert?.name}
                  {webinar?.expert?.organization && ` · ${webinar?.expert?.organization}`}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-muted rounded-lg transition-colors flex-shrink-0 ml-3"
            aria-label="Close video player"
          >
            <Icon name="X" size={18} color="var(--color-foreground)" />
          </button>
        </div>

        {/* Video Area */}
        <div className="flex-1 min-h-0 bg-black rounded-b-xl overflow-hidden">
          {!recordingUrl ? (
            <div className="flex flex-col items-center justify-center h-64 text-center px-6">
              <Icon name="VideoOff" size={40} color="var(--color-muted-foreground)" />
              <p className="mt-3 text-muted-foreground text-sm">Recording not available yet</p>
            </div>
          ) : iframeError ? (
            <div className="flex flex-col items-center justify-center h-64 text-center px-6 gap-4">
              <Icon name="AlertCircle" size={40} color="var(--color-warning)" />
              <p className="text-white text-sm">Unable to embed video. Open in a new tab instead.</p>
              <a
                href={recordingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors"
              >
                Open Recording
              </a>
            </div>
          ) : type === 'youtube' || type === 'vimeo' ? (
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={embedUrl}
                title={webinar?.title}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                onError={() => setIframeError(true)}
              />
            </div>
          ) : type === 'direct' ? (
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <video
                src={embedUrl}
                controls
                autoPlay
                className="absolute inset-0 w-full h-full"
                onError={() => setIframeError(true)}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center px-6 gap-4">
              <Icon name="Link" size={40} color="var(--color-muted-foreground)" />
              <p className="text-white text-sm">
                This recording link cannot be embedded. Open it directly:
              </p>
              <a
                href={recordingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors"
              >
                Open Recording
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoPlayerModal;
